import { useAuth } from '../contexts/AuthContext'; // Import useAuth
import { useCallback } from 'react';

/**
 * Custom hook to provide an analytics sending function.
 * This hook encapsulates the logic for checking admin status 
 * before sending an event.
 * 
 * @returns {function} A function `(event, properties) => void` to send analytics events.
 */
export const useSa = () => {
  const { isAdmin } = useAuth(); // Call hook inside the custom hook

  // Return a memoized function to send events
  const sendAnalyticsEvent = useCallback((event, properties) => {
    if (isAdmin) return; // Use isAdmin from the hook's scope

    if (typeof window.sa_event !== "function") {
      console.warn("Analytics function (sa_event) not found on window object.");
      return;
    }
    
    // eslint-disable-next-line no-undef
    sa_event(event, properties);

  }, [isAdmin]); // Dependency array ensures the function identity is stable unless isAdmin changes

  return sendAnalyticsEvent;
};

// File Helper
export const downloadFile = (url) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = url.split("/").pop();
  a.click();
};

// Size Helper
export const toReadableSize = (size) => {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (size <= 0) return "0 bytes";
  const i = parseInt(Math.floor(Math.log(size) / Math.log(1024)));
  return `${(size / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

// CSS Animations - Consider moving to a global CSS file or CSS-in-JS solution
export const pulseAnimation = `
  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.7); }
    70% { box-shadow: 0 0 0 10px rgba(99, 102, 241, 0); }
    100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); }
  }
`;

export const defaultCompressionOptions = {
  texture: {
    enabled: true,
    format: 'webp',
    resize: [1024, 1024],
    quality: 80
  },
  draco: {
    enabled: true,
    compressionLevel: 7,
    quantizationBits: 14
  },
  transforms: {
    deduplicate: true,
    removeUnused: true,
    instance: true,
    flatten: true,
    merge: true,
    weld: true,
    simplify: 0.0
  }
};

export const MAX_FILE_SIZE_MB = 500; 
